import { useEffect, useRef, useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";

// Try to import logout action if it exists
let logoutAction = null;

try {
  // eslint-disable-next-line global-require
  ({ logout: logoutAction } = require("../redux/authSlice"));
} catch (e) {
  logoutAction = null;
}

// ======================================================
// CONFIG
// ======================================================

const IDLE_TIMEOUT_MS = 10 * 60 * 1000; // 10 minutes

// Show warning 30 seconds before automatic logout
const WARNING_TIMEOUT_MS = 30 * 1000; // 30 seconds

// Check countdown every second
const COUNTDOWN_INTERVAL_MS = 1000;

// ======================================================
// ACTIVITY EVENTS
// ======================================================

const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "click",
];

// ======================================================
// COMPONENT
// ======================================================

const AutoLogout = () => {
  const dispatch = useDispatch();

  const idleTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);

  const [showWarning, setShowWarning] = useState(false);
  const [countdown, setCountdown] = useState(30);

  // Ref mirror of showWarning — resetTimer reads this instead of the state
  // value directly, so resetTimer's identity stays stable and DOESN'T force
  // the main useEffect below to re-run (and tear down the countdown interval
  // via clearAllTimers()) the moment the warning modal opens.
  const showWarningRef = useRef(false);

  useEffect(() => {
    showWarningRef.current = showWarning;
  }, [showWarning]);

  // Set when the 10-min idle timeout fires while the tab is in the
  // background. The countdown doesn't start yet in that case — we wait
  // until the tab is actually visible again (see visibilitychange handler
  // below), since background setTimeout/setInterval get throttled by the
  // browser and can't be trusted for an accurate 30-second countdown.
  const pendingWarningRef = useRef(false);

  // ====================================================
  // CLEAR ALL TIMERS
  // ====================================================

  const clearAllTimers = useCallback(() => {
    pendingWarningRef.current = false;

    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }

    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }

    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  // ====================================================
  // FORCE LOGOUT
  // ====================================================

  const forceLogout = useCallback(() => {
    clearAllTimers();

    const hadToken = !!localStorage.getItem("token");

    // Clear authentication data
    localStorage.removeItem("token");
    localStorage.removeItem("role");

    // Clear Redux auth state if logout action exists
    if (logoutAction) {
      try {
        dispatch(logoutAction());
      } catch (error) {
        console.warn("AutoLogout: dispatch(logout()) failed", error);
      }
    }

    setShowWarning(false);

    if (hadToken) {
      toast.info("You were logged out due to 10 minutes of inactivity.");
    }

    // Redirect to login
    window.location.href = "/login";
  }, [clearAllTimers, dispatch]);

  // ====================================================
  // SHOW WARNING MODAL
  // ====================================================

  const beginWarningCountdown = useCallback(() => {
    // Don't show warning if user is already logged out
    if (!localStorage.getItem("token")) {
      return;
    }

    setCountdown(30);
    setShowWarning(true);

    // Clear existing countdown if any
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }

    let remainingSeconds = 30;

    countdownIntervalRef.current = setInterval(() => {
      remainingSeconds -= 1;

      setCountdown(remainingSeconds);

      if (remainingSeconds <= 0) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;

        forceLogout();
      }
    }, COUNTDOWN_INTERVAL_MS);
  }, [forceLogout]);

  const showLogoutWarning = useCallback(() => {
    if (!localStorage.getItem("token")) {
      return;
    }

    // Tab is in the background right now — don't start the 30s countdown
    // yet. Just remember that a warning is due; visibilitychange below
    // will kick off the real countdown the moment the user actually comes
    // back to this tab.
    if (document.visibilityState === "hidden") {
      pendingWarningRef.current = true;
      return;
    }

    beginWarningCountdown();
  }, [beginWarningCountdown]);

  // ====================================================
  // RESET IDLE TIMER
  // ====================================================

  const resetTimer = useCallback(() => {
    // Don't run timer if user isn't logged in
    if (!localStorage.getItem("token")) {
      clearAllTimers();
      return;
    }

    // If warning modal is already visible,
    // don't silently cancel it through mouse/keyboard activity.
    if (showWarningRef.current) {
      return;
    }

    // Clear existing timers
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }

    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
    }

    // Start fresh 10-minute timer
    idleTimerRef.current = setTimeout(() => {
      showLogoutWarning();
    }, IDLE_TIMEOUT_MS);
  }, [clearAllTimers, showLogoutWarning]);

  // ====================================================
  // STAY LOGGED IN
  // ====================================================

  const handleStayLoggedIn = useCallback(() => {
    pendingWarningRef.current = false;

    // Close warning modal
    setShowWarning(false);

    // Reset countdown
    setCountdown(30);

    // Clear countdown interval
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }

    // Clear warning timer
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }

    // Start fresh 10-minute inactivity timer
    if (localStorage.getItem("token")) {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current);
      }

      idleTimerRef.current = setTimeout(() => {
        showLogoutWarning();
      }, IDLE_TIMEOUT_MS);
    }
  }, [showLogoutWarning]);

  // ====================================================
  // INITIAL SETUP
  // ====================================================

  useEffect(() => {
    // Start timer on mount
    resetTimer();

    // Listen for user activity
    const handleActivity = () => {
      resetTimer();
    };

    ACTIVITY_EVENTS.forEach((event) => {
      window.addEventListener(event, handleActivity, {
        passive: true,
      });
    });

    // ==================================================
    // CROSS-TAB LOGOUT SYNC
    // ==================================================

    const handleStorage = (event) => {
      // Another tab removed token
      if (event.key === "token" && !event.newValue) {
        clearAllTimers();
        setShowWarning(false);

        window.location.href = "/login";
      }

      // Another tab logged in
      if (event.key === "token" && event.newValue) {
        resetTimer();
      }
    };

    window.addEventListener("storage", handleStorage);

    // ==================================================
    // TAB BECOMES VISIBLE AGAIN → start the countdown if
    // it was pending while the tab was in the background
    // ==================================================

    const handleVisibility = () => {
      if (document.visibilityState === "visible" && pendingWarningRef.current) {
        pendingWarningRef.current = false;
        beginWarningCountdown();
      }
    };

    document.addEventListener("visibilitychange", handleVisibility);

    // ==================================================
    // CLEANUP
    // ==================================================

    return () => {
      ACTIVITY_EVENTS.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });

      window.removeEventListener("storage", handleStorage);
      document.removeEventListener("visibilitychange", handleVisibility);

      clearAllTimers();
    };
  }, [clearAllTimers, resetTimer, beginWarningCountdown]);

  // ====================================================
  // WARNING MODAL
  // ====================================================

  if (!showWarning) {
    return null;
  }

  return (
    <>
      {/* Overlay */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(15, 23, 42, 0.65)",
          backdropFilter: "blur(3px)",
          WebkitBackdropFilter: "blur(3px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 99999,
          padding: "20px",
        }}
      >
        {/* Modal */}
        <div
          style={{
            width: "100%",
            maxWidth: "430px",
            background: "#ffffff",
            borderRadius: "16px",
            padding: "32px",
            boxShadow: "0 20px 50px rgba(0, 0, 0, 0.25)",
            textAlign: "center",
            fontFamily:
              '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif',
            animation: "autoLogoutFadeIn 0.2s ease-out",
          }}
        >
          {/* Icon */}
          <div
            style={{
              width: "64px",
              height: "64px",
              margin: "0 auto 18px",
              borderRadius: "50%",
              background: "#fff7ed",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "30px",
            }}
          >
            ⏱️
          </div>

          {/* Title */}
          <h2
            style={{
              margin: "0 0 10px",
              fontSize: "22px",
              fontWeight: 600,
              color: "#111827",
            }}
          >
            Session Timeout
          </h2>

          {/* Description */}
          <p
            style={{
              margin: "0 auto 20px",
              maxWidth: "340px",
              fontSize: "15px",
              lineHeight: 1.6,
              color: "#6b7280",
            }}
          >
            You have been inactive for a while.
            <br />
            You will be automatically logged out in:
          </p>

          {/* Countdown */}
          <div
            style={{
              width: "86px",
              height: "86px",
              margin: "0 auto 20px",
              borderRadius: "50%",
              border: "5px solid #f97316",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              background: "#fff7ed",
            }}
          >
            <div
              style={{
                fontSize: "30px",
                lineHeight: 1,
                fontWeight: 700,
                color: "#ea580c",
              }}
            >
              {countdown}
            </div>

            <div
              style={{
                marginTop: "5px",
                fontSize: "11px",
                color: "#9a3412",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              seconds
            </div>
          </div>

          {/* Security message */}
          <p
            style={{
              margin: "0 0 24px",
              fontSize: "13px",
              color: "#9ca3af",
            }}
          >
            For your security, your session will expire automatically.
          </p>

          {/* Stay Logged In Button */}
          <button
            type="button"
            onClick={handleStayLoggedIn}
            style={{
              width: "100%",
              height: "48px",
              border: "none",
              borderRadius: "10px",
              background: "#2563eb",
              color: "#ffffff",
              fontSize: "15px",
              fontWeight: 600,
              cursor: "pointer",
              transition: "background 0.2s ease",
              boxShadow: "0 4px 10px rgba(37, 99, 235, 0.25)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#1d4ed8";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#2563eb";
            }}
          >
            Stay Logged In
          </button>

          {/* Small footer */}
          <div
            style={{
              marginTop: "14px",
              fontSize: "12px",
              color: "#9ca3af",
            }}
          >
            Your session will remain active.
          </div>
        </div>
      </div>

      {/* Modal animation */}
      <style>
        {`
          @keyframes autoLogoutFadeIn {
            from {
              opacity: 0;
              transform: translateY(10px) scale(0.98);
            }

            to {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }
        `}
      </style>
    </>
  );
};

export default AutoLogout;
